import React, { useState } from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { NavLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import MainContent from '../components/MainContent';
import CreateNotification from '../components/CreateNotification';
import Modal from '../components/Modal';
import Spinner from '../components/spinner/Spinner';
import TableWrapper from '../utils/TableWrapper';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

function PushNotifications() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    message: '',
    html_content: '',
  });

  const queryClient = useQueryClient();
  const { userDetails } = useSelector((state) => state.auth);

  const isAdmin =
    userDetails?.role === 'Admin' ||
    userDetails?.role === 'admin' ||
    userDetails?.role === 'super_admin' ||
    userDetails?.role === 'SuperAdmin';
  const isModuleLead = userDetails?.role === 'ModuleLead';

  const canEditDelete = (notification) => {
    if (isAdmin) return true;
    // Module Leads can edit/delete their own notifications
    if (isModuleLead && notification?.sender?.id === userDetails?.id)
      return true;
    return notification?.sender?.id === userDetails?.id;
  };

  const toggleModal = () => setModalOpen(!modalOpen);
  const toggleViewModal = () => setViewModalOpen(!viewModalOpen);
  const toggleEditModal = () => setEditModalOpen(!editModalOpen);
  const toggleDeleteModal = () => setDeleteModalOpen(!deleteModalOpen);

  const getTypeStyles = (type) => {
    if (type === 'urgent') return 'bg-red-100 text-red-800';
    if (type === 'high') return 'bg-orange-100 text-orange-800';
    return 'bg-blue-100 text-blue-800';
  };

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return axiosInstancev2.delete(`/notifications/push/delete?id=${id}`);
    },
    onSuccess: () => {
      toast.success('Notification deleted successfully');
      queryClient.invalidateQueries(['notifications']);
      toggleDeleteModal();
      setSelectedNotification(null);
    },
    onError: (error) => {
      toast.error(error?.response?.data || 'Failed to delete notification');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return axiosInstancev2.put(`/notifications/push/update?id=${id}`, data);
    },
    onSuccess: () => {
      toast.success('Notification updated successfully');
      queryClient.invalidateQueries(['notifications']);
      toggleEditModal();
      setSelectedNotification(null);
    },
    onError: (error) => {
      toast.error(error?.response?.data || 'Failed to update notification');
    },
  });

  const handleEdit = (notification) => {
    setSelectedNotification(notification);
    setEditForm({
      title: notification.title || '',
      message: notification.message || '',
      html_content: notification.html_content || '',
    });
    toggleEditModal();
  };

  const handleDelete = (notification) => {
    setSelectedNotification(notification);
    toggleDeleteModal();
  };

  const confirmDelete = () => {
    if (selectedNotification?.id) {
      deleteMutation.mutate(selectedNotification.id);
    }
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    if (selectedNotification?.id) {
      updateMutation.mutate({
        id: selectedNotification.id,
        data: editForm,
      });
    }
  };

  const columns = [
    {
      title: 'Title',
      field: 'title',
      render: (row) => <p className="font-medium text-gray-900">{row.title}</p>,
    },
    {
      title: 'Type',
      field: 'type',
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${getTypeStyles(
            row.type
          )}`}
        >
          {row.type}
        </span>
      ),
    },
    {
      title: 'Target',
      field: 'target_type',
      render: (row) => (
        <span className="capitalize text-gray-600 text-sm">
          {row.target_type}
        </span>
      ),
    },
    {
      title: 'Sent By',
      field: 'sender',
      render: (row) => (
        <span className="text-gray-600 text-sm">
          {row.sender?.email || 'System'}
        </span>
      ),
    },
    {
      title: 'Sent At',
      field: 'sent_at',
      render: (row) => (
        <span className="text-gray-500 text-sm">
          {row.sent_at
            ? format(new Date(row.sent_at), 'MMM d, yyyy h:mm a')
            : '-'}
        </span>
      ),
    },
    {
      title: 'Stats',
      render: (row) => (
        <div className="flex gap-3 text-sm text-gray-600">
          <span title="Delivered">
            📤 {row.delivery_stats?.total_delivered || 0}
          </span>
          <span title="Read">👁 {row.delivery_stats?.total_read || 0}</span>
          <span title="Clicked">
            🖱 {row.delivery_stats?.total_clicked || 0}
          </span>
        </div>
      ),
    },
    {
      title: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            onClick={() => {
              setSelectedNotification(row);
              toggleViewModal();
            }}
          >
            View
          </button>
          {canEditDelete(row) && (
            <>
              <button
                type="button"
                className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                onClick={() => handleEdit(row)}
              >
                Edit
              </button>
              <button
                type="button"
                className="text-red-600 hover:text-red-800 text-sm font-medium"
                onClick={() => handleDelete(row)}
              >
                Delete
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const getNotifications = async () => {
    const response = await axiosInstancev2.get('/notifications/push');
    return response;
  };

  const { data, isLoading } = useQuery(['notifications'], getNotifications, {
    staleTime: 60000,
    cacheTime: 300000,
  });

  return (
    <>
      <MainContent full>
        <div className="bg-white px-4 py-5 rounded-lg flex justify-between items-center shadow-sm">
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon />}>
            <NavLink
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              to="/"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              <p>Home</p>
            </NavLink>
            <p className="text-gray-900 font-medium">Push Notifications</p>
          </Breadcrumbs>

          <button
            type="button"
            className="bg-claret-500 hover:bg-claret-600 text-white text-sm font-medium py-2 px-6 rounded-lg transition-colors shadow-sm flex items-center"
            onClick={toggleModal}
          >
            <span className="mr-2">+</span> Create Notification
          </button>
        </div>

        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="w-full h-64 flex justify-center items-center">
              <Spinner />
            </div>
          ) : (
            <TableWrapper
              title="Push Notification History"
              columns={columns}
              data={(data?.data?.notifications || []).filter((notification) => {
                if (isAdmin) return true;
                return notification?.sender?.id === userDetails?.id;
              })}
              emptyMessage="No notifications sent yet"
            />
          )}
        </div>
      </MainContent>

      <Modal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        title="Send Push Notification"
        width="max-w-4xl"
      >
        <CreateNotification toggleModal={toggleModal} />
      </Modal>

      <Modal
        modalOpen={viewModalOpen}
        toggleModal={toggleViewModal}
        title="Notification Details"
      >
        {selectedNotification && (
          <div className="space-y-6">
            <div className="border-b border-gray-100 pb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {selectedNotification.title}
              </h3>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>
                  Sent:{' '}
                  {selectedNotification.sent_at
                    ? format(new Date(selectedNotification.sent_at), 'PPP p')
                    : 'Draft'}
                </span>
                <span className="capitalize px-2 py-0.5 bg-gray-100 rounded-full text-xs">
                  {selectedNotification.type}
                </span>
              </div>
              {selectedNotification.sender && (
                <p className="text-sm text-gray-500 mt-1">
                  By: {selectedNotification.sender.email}
                </p>
              )}
            </div>

            <div className="prose prose-sm max-w-none text-gray-700">
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    selectedNotification.html_content ||
                    selectedNotification.message,
                }}
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                Delivery Statistics
              </h4>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-white p-3 rounded shadow-sm">
                  <div className="text-2xl font-bold text-gray-600">
                    {selectedNotification.delivery_stats?.total_targeted || 0}
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">
                    Targeted
                  </div>
                </div>
                <div className="bg-white p-3 rounded shadow-sm">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedNotification.delivery_stats?.total_delivered || 0}
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">
                    Delivered
                  </div>
                </div>
                <div className="bg-white p-3 rounded shadow-sm">
                  <div className="text-2xl font-bold text-green-600">
                    {selectedNotification.delivery_stats?.total_read || 0}
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">
                    Read
                  </div>
                </div>
                <div className="bg-white p-3 rounded shadow-sm">
                  <div className="text-2xl font-bold text-purple-600">
                    {selectedNotification.delivery_stats?.total_clicked || 0}
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">
                    Clicked
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={toggleViewModal}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        modalOpen={editModalOpen}
        toggleModal={toggleEditModal}
        title="Edit Notification"
      >
        <form onSubmit={handleUpdateSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={editForm.title}
              onChange={(e) =>
                setEditForm({ ...editForm, title: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-claret-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={editForm.message}
              onChange={(e) =>
                setEditForm({ ...editForm, message: e.target.value })
              }
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-claret-500 focus:border-transparent"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={toggleEditModal}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isLoading}
              className="px-4 py-2 bg-claret-500 hover:bg-claret-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        modalOpen={deleteModalOpen}
        toggleModal={toggleDeleteModal}
        title="Delete Notification"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete the notification &quot;
            {selectedNotification?.title}&quot;? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={toggleDeleteModal}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleteMutation.isLoading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default PushNotifications;
